/* eslint-disable @typescript-eslint/naming-convention */
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterEvent, UrlSerializer } from '@angular/router';
import { MmAlertService } from '@metromarkets/components';
import { TranslateService } from '@ngx-translate/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, skip, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import Url from 'url-parse';
import {
  ACCOUNT_TYPE_KEY,
  IMPERSONATED_CUSTOMER_FULLNAME_KEY,
  LOGIN_USER_TYPE_LOCAL_STORAGE_KEY,
  LOGIN_USER_TYPES,
  METROMARKETS_IMPERSONATE_BUYER,
  SOFT_LOGIN_TOKEN_KEY,
  SUB_ACCOUNT_TYPE_KEY,
  USER_TYPE_KEY
} from '~app/core/modules/identity/constants/identity.constant';
import { AccountInfoService } from '~app/core/modules/identity/services/account-info.service';
import { IdentityService } from '~app/core/modules/identity/services/identity.service';
// prettier-ignore
import {
  AddressMetroXHttpService
} from '~app/modules/account/modules/address-book/services/address-metro-x-http.service';
import { AccountFlyoutService } from '~app/modules/account/services/account-flyout.service';
import { AccountFlyoutUtils } from '~app/modules/account/utils';
import { TierTwoRegistrationData } from '~app/modules/authorization/models/credentials.model';
import {
  AccountRegistrationResponse,
  AccountRegistrationSuccess
} from '~app/modules/authorization/models/css-api-response.model';
import { CheckoutCommonUtil } from '~app/modules/checkout/utils/checkout-common-util';
// prettier-ignore
import {
  AccountFlyoutFailoverService
} from '~app/modules/generic/components/header/account-flyout-failover/account-flyout-failover.service';
import { environment } from '~env/environment';
import {
  cleanupAuthSession,
  isHeadQuarter,
  isMMCompanion,
  isOTPLoggedIn,
  isSoftLoggedIn
} from '~server/utils/auth.utils';
import {
  ApiConfig,
  APP_CATALOG_ROUTE,
  CHECKOUT_STEPS_ROUTE,
  FeatureToggleEnum,
  NUMBER_1,
  ORDER_STEP_ADDRESS
} from '~shared/constants';
import { CountryCode } from '~shared/constants/country-code.enum';
import {
  ACCOUNT_ADD_BUSINESS_LINK,
  ADD_METRO_CARD_URL,
  MAGIC_LINK_EXPIRY_QUERY_PARAM,
  PASSWORDLESS_INTERMEDIATE_URL,
  RESET_PASSWORD_SUCCESS_QUERY_PARAM
} from '~shared/constants/links.constants';
import { QueryParamsEncoder } from '~shared/helpers/query-encoder';
import { IAccountInformation, IAccountInformationV2 } from '~shared/models';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import {
  ApplicationAuthorizationActionType as AuthType,
  RegistrationTypeLabels
} from '~shared/modules/application-event/constants';
// prettier-ignore
import {
  ApplicationIdamRegisterEventTypeEnum
} from '~shared/modules/application-event/constants/application-idam-register-event-type.enum';
import { IdamRegisterApplicationEvent } from '~shared/modules/application-event/models';

import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { LoggerStrategies } from '~shared/modules/logger/models';
import { LoggerService } from '~shared/modules/logger/services/logger.service';
import { ServerLoggerService } from '~shared/modules/server-logger/services/server-logger.service';
import { ExponeaService } from '~shared/services/exponea.service';
import { MarketDetectionUtil } from '~shared/utils/market-detection.util';
import { UrlUtil } from '~shared/utils/url.util';
import { uuidv4 } from '~shared/utils/uuid.util';
import {
  authCSSEndpoints,
  AUTHORIZATION_NOTIFICATIONS,
  authorizationEndpoints,
  createAuthConfig,
  FF_FLYOUT_WEB_COMPONENT,
  FF_LOGIN_MONITORING_LOGGING_STRATEGY,
  FF_REMOVE_REACT_COOKIE,
  FF_SAVE_LEGAL_ADDRESS_AS_BILLING_AND_SHIPPING,
  FF_SL_CODE_LOGIN_DISABLE_SR,
  FF_SL_ENABLE_FALLBACK_FLYOUT_FOR_HEADQUATERS,
  OAUTH_ERRORS,
  USER_TYPE
} from '../constants/authorization.constant';
import { Tier1Response } from '../constants/idam-register.enum';
import { IS_SAVE_LEGAL_ADDRESS_AS_BILLING_AND_SHIPPING } from '../constants/registration-tier-two-constants';
import { AuthorizationHttpService, IMagicLinkPayload, IResetPasswordPayload } from './authorization-http.service';

const SIGNUP_SUCCESS_REDIRECT_PATH_KEY = 'signup-success-redirect-path';
export const BACK_URL_KEY = 'backUrl';

export const logRequestWaitingTime = 1000;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  currentCountry: string;
  private readonly IDAM_SSO_REALM = environment.IDAMSSO() && environment.IDAMSSO().realm;
  private contextSwitch$ = new Subject<void>();
  private flyoutRefreshSubject$ = new Subject<void>();
  private logoutEvent$ = new Subject<void>();
  private isOTPLoggedIn$ = this.activeFeatureToggleService
    .isFeatureEnabledReadyForCurrentCountry$(FF_SL_CODE_LOGIN_DISABLE_SR)
    .pipe(map((on) => on && isOTPLoggedIn()));
  private isHeadQuarter$ = this.activeFeatureToggleService
    .isFeatureEnabledReadyForCurrentCountry$(FF_SL_ENABLE_FALLBACK_FLYOUT_FOR_HEADQUATERS)
    .pipe(map((on) => on && isHeadQuarter()));
  private isWebFlyoutEnabled$ = combineLatest([
    this.activeFeatureToggleService.isFeatureEnabledReadyForCurrentCountry$(FF_FLYOUT_WEB_COMPONENT),
    this.isOTPLoggedIn$,
    this.isHeadQuarter$,
  ]).pipe(
    take(NUMBER_1),
    map(
      ([isWebFlyoutEnabled, isOtpLoginDone, isHQ]) =>
        isWebFlyoutEnabled && !isOtpLoginDone && !isSoftLoggedIn() && !isHQ && !isMMCompanion()
    )
  );
  private isSaveLegalAddressAsBillingAndShippingEnabled$ =
    this.activeFeatureToggleService.isFeatureEnabledReadyForCurrentCountry$(
      FF_SAVE_LEGAL_ADDRESS_AS_BILLING_AND_SHIPPING
    );
  private isReactPLPsEnabled: boolean;
  protected unsubscribe$ = new Subject();

  constructor(
    private oAuthService: OAuthService,
    private identityService: IdentityService,
    private authorizationHttpService: AuthorizationHttpService,
    private httpClient: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private serializer: UrlSerializer,
    private translateService: TranslateService,
    private alertService: MmAlertService,
    private accountFlyout: AccountFlyoutService,
    private accountFlyoutFailover: AccountFlyoutFailoverService,
    private queryParamsEncoder: QueryParamsEncoder,
    private exponeaService: ExponeaService,
    private activeFeatureToggleService: ActiveFeaturesConfigcatService,
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) private platformId: Object,
    private applicationEventService: ApplicationEventService,
    private serverLoggerService: ServerLoggerService,
    private accountInfoService: AccountInfoService,
    private addressMetroXHttpService: AddressMetroXHttpService,
    private loggerService: LoggerService,
  ) {
    this.currentCountry = MarketDetectionUtil.getCountryCode();
    this.subscribeToRegisterCardGAEvent();
    this.authListener();
    this.activeFeatureToggleService
      .isFeatureEnabledReadyForCurrentCountry$(FeatureToggleEnum.FF_PLP_REACT_MIGRATION)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isEnabled: boolean): void => {
        this.isReactPLPsEnabled = isEnabled;
      });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  // prettier-ignore
  public login( // NOSONAR
    redirectPath?: string,
    prefilledEmailId?: string,
    isPasswordReset?: boolean,
    isTierThreeSignupForPasswordLessFlow?: boolean,
    isBatchTier2Registration?: boolean
  ) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const backUrl = new URL(document.location.toString()).searchParams.get('backUrl');

    localStorage.setItem(BACK_URL_KEY, backUrl ?? '/');
    const signupRedirectPath = `${redirectPath}?backUrl=${backUrl}`;

    localStorage.setItem(SIGNUP_SUCCESS_REDIRECT_PATH_KEY, signupRedirectPath);

    let authConfig = createAuthConfig();
    const redirectUrlForNormalFlow = this.getRedirectUrlForNormalFlow(
      redirectPath, authConfig, isTierThreeSignupForPasswordLessFlow);

    const redirectUri = this.getRedirectUrlBasedOnPasswordLessFlow(
      isTierThreeSignupForPasswordLessFlow && !isBatchTier2Registration,
      redirectPath,
      redirectUrlForNormalFlow
    );

    authConfig = { ...authConfig, redirectUri };
    authConfig = this.setOAuthConfig(isPasswordReset, authConfig);
    this.tryOAuthConfiguration(authConfig);

    this.oAuthService
      .loadDiscoveryDocumentAndTryLogin()
      .then(() => {
        this.identityService.isAuthorized$
          .pipe(take(NUMBER_1))
          .pipe(
            switchMap((authorized) =>
              this.activeFeatureToggleService
                .getFeatureValue(FF_LOGIN_MONITORING_LOGGING_STRATEGY, LoggerStrategies.instanaLogger)
                .pipe(map((strategy) => ({ strategy, authorized })))
            )
          )
          .subscribe(({ authorized, strategy }) => {
            this.tryInitCodeFlow(authorized, prefilledEmailId, isPasswordReset, strategy);
          });
      })
      .catch((error) => {
        this.logError(error, OAUTH_ERRORS.loadDiscoveryDocumentAndTryLogin);
      });
  }

  setOAuthConfig(isPasswordReset, authConfig) {
    if (isPasswordReset) {
      authConfig = {
        ...authConfig,
        openUri: () => {
          window.location.href = this.getPasswordResetUrl(authConfig);
        },
      };
    }

    return authConfig;
  }

  tryOAuthConfiguration(authConfig) {
    try {
      this.oAuthService.configure(authConfig);
    } catch (error) {
      this.logError(error, OAUTH_ERRORS.oAuthService);
    }
  }

  authListener() {
    if (isPlatformBrowser(this.platformId)) {
      this.logoutListener().pipe(tap(cleanupAuthSession)).subscribe();
    }
  }

  logError(error, message) {
    this.activeFeatureToggleService
      .getFeatureValue(FF_LOGIN_MONITORING_LOGGING_STRATEGY, LoggerStrategies.instanaLogger)
      .subscribe((strategy) => {
        this.loggerService.error(error, message, strategy);
      });
  }

  getPasswordResetUrl(authConfig) {
    const passwordResetUrl =
      `${environment.IDAMSSO().issuer}/web/forgot-password?` +
      `client_id=${environment.IDAMSSO().clientId}` +
      `&realm_id=${this.IDAM_SSO_REALM}&country_code=${this.currentCountry.toUpperCase()}` +
      `&response_type=code&user_type=${USER_TYPE}&redirect_uri=${authConfig.redirectUri}`;

    return passwordResetUrl;
  }

  getRedirectUrlForNormalFlow(redirectPath, authConfig, isTierThreeSignupForPasswordLessFlow) {
    if (isTierThreeSignupForPasswordLessFlow) {
      return redirectPath;
    } else {
      return redirectPath
        ? `${location.origin}${environment.baseHref() || '/'}${redirectPath.replace(/^\//, '')}`
        : authConfig.redirectUri;
    }
  }

  getRedirectUrlBasedOnPasswordLessFlow(isTierThreeSignupForPasswordLessFlow, redirectPath, redirectUrlForNormalFlow) {
    return isTierThreeSignupForPasswordLessFlow
      ? `${environment.myMetroBaseUrl()}${redirectPath}`
      : redirectUrlForNormalFlow;
  }

  tryInitCodeFlow(authorized, prefilledEmailId, isPasswordReset, strategy) {
    if (!authorized) {
      try {
        const inputParams = this.getInputParams();
        const loginId = prefilledEmailId ? prefilledEmailId : '';
        if (!isPasswordReset) {
          Object.assign(inputParams, { login_id: loginId });
        }

        const isEmployee = inputParams.user_type === LOGIN_USER_TYPES.EMPLOYEE;

        if (isEmployee) {
          this.oAuthService.scope = `openid role=${METROMARKETS_IMPERSONATE_BUYER}`;
        }
        this.oAuthService.initCodeFlow('', inputParams);
      } catch (error) {
        this.loggerService.error(error, OAUTH_ERRORS.initCodeFlow, strategy);
      }
    }
  }

  getInputParams() {
    const user_type = localStorage.getItem(LOGIN_USER_TYPE_LOCAL_STORAGE_KEY) ?? LOGIN_USER_TYPES.CUSTOMER;

    return {
      realm_id: this.IDAM_SSO_REALM,
      country_code: this.currentCountry.toUpperCase(),
      locale_id: MarketDetectionUtil.getIDAMLocale(),
      user_type,
    };
  }

  public handleOAuthLogout(): void {
    this.oAuthService.logOut(true);
    this.logoutEvent$.next();
  }

  public logout(): void {
    this.oAuthService.stopAutomaticRefresh();

    const logoutIframe = document.createElement('iframe');
    logoutIframe.id = 'idam-iframe-op_seassion_end';
    logoutIframe.src = `${this.oAuthService.logoutUrl}?id_token_hint=${this.oAuthService.getIdToken()}`;
    document.body.append(logoutIframe);

    this.activeFeatureToggleService
      .isFeatureEnabledReadyForCurrentCountry$(FF_REMOVE_REACT_COOKIE)
      .subscribe((isRemoveReactCookieEnabled: boolean) => {
        if (isRemoveReactCookieEnabled) {
          const url = ApiConfig.BUYER_REACT_COOKIE_CLEAR_API_URL();
          this.httpClient
            .get(url)
            .pipe(catchError(() => of(false)))
            .subscribe(() => {
              this.oAuthService.logOut(true);
              this.logoutEvent$.next();
            });
        } else {
          this.oAuthService.logOut(true);
          this.logoutEvent$.next();
        }
      });

    localStorage.removeItem(USER_TYPE_KEY);
    localStorage.removeItem(ACCOUNT_TYPE_KEY);
    localStorage.removeItem(SUB_ACCOUNT_TYPE_KEY);
    CheckoutCommonUtil.clearSubsidiaryNeedsApprovalFromLocalStorage()
    localStorage.removeItem(IMPERSONATED_CUSTOMER_FULLNAME_KEY);
    localStorage.removeItem(SOFT_LOGIN_TOKEN_KEY);
  }

  public validateEmail(email) {
    return this.authorizationHttpService.validateEmail(email);
  }

  // prettier-ignore
  public register( // NOSONAR
    email,
    password,
    registerBusiness,
    passwordless,
    isTierThreeSignupForPasswordLessFlow?: boolean,
    isBatchTier2Registration?: boolean,
    tier2PasswordlessRedirectionRoute?: string,
  ): Observable<any> {
    const [redirectURI, redirectPath, autoLoginRedirectURI, autoLoginRedirect]: string[] =
      (isTierThreeSignupForPasswordLessFlow || isBatchTier2Registration)
        ? this.getRedirectURIForPasswordLessFlow(isBatchTier2Registration, isTierThreeSignupForPasswordLessFlow, tier2PasswordlessRedirectionRoute)
        : this.getRedirectURI(registerBusiness);
    return this.authorizationHttpService.IDAMRegister(email, password, passwordless).pipe(
      switchMap((response: Tier1Response) =>
        this.triggerLoginAfterSignup(
          email,
          password,
          response.challenge,
          redirectURI,
          autoLoginRedirectURI,
          autoLoginRedirect,
          passwordless
        )
      ),
      tap(() => {
        this.oAuthService
          .silentRefresh({ realm_id: this.IDAM_SSO_REALM, country_code: this.currentCountry.toUpperCase() })
          .then((event) => {
            this.identityService.isAuthorized$.pipe(filter((authorized) => authorized)).subscribe((authorized) => {
              localStorage.removeItem(SIGNUP_SUCCESS_REDIRECT_PATH_KEY);

              this.exponeaService.sendNewCustomerData('registration', email);

              const redirectUrl = new Url(decodeURIComponent(redirectPath), true);

              if (isPlatformBrowser(this.platformId)) {
                this.serverLoggerService.info('TIER1 SIGNUP: successful login', {
                  correlationId: autoLoginRedirect,
                });
              }

              localStorage.removeItem(BACK_URL_KEY);

              this.activeFeatureToggleService
                .isFeatureEnabledReadyForCurrentCountry$(FeatureToggleEnum.FF_SL_NG_REGISTER_EXTERNAL_REDIRECTION)
                .subscribe((enabled) => {
                  if (enabled) {
                    if (isTierThreeSignupForPasswordLessFlow) {
                      window.location.href = `${redirectURI}?redirectUrl=${encodeURIComponent(redirectPath)}`;
                    } else {
                      UrlUtil.redirectToExternalPage(redirectUrl.pathname, this.getQueryParams(redirectUrl));
                    }
                  } else {
                    if (isTierThreeSignupForPasswordLessFlow) {
                      window.location.href = `${redirectURI}?redirectUrl=${encodeURIComponent(redirectPath)}`;
                    } else if (redirectUrl.pathname === '/') {
                      UrlUtil.redirectToReactHomePage(this.getQueryParams(redirectUrl));
                    } else {
                  this.activeFeatureToggleService.isFeatureEnabledReadyForCurrentCountry$(
                    FeatureToggleEnum.FF_EXTERNAL_CHECKOUT_ADDRESS_STEP
                  ).subscribe((enabled) => {
                          if (enabled && redirectUrl.pathname === '/checkout/checkout/order/address') {
                            this.navigateToExternalAddress();
                          } else {
                            this.router.navigate([redirectUrl.pathname], {
                              replaceUrl: true,
                              queryParams: this.getQueryParams(redirectUrl),
                            });
                          }
                        });
                    }
                  }
                });
              });
            this.accountInfoService
              .getAccountInfo()
              .pipe(
                // eslint-disable-next-line no-magic-numbers
                skip(1),
                filter((accountInfo) => accountInfo === null),
                // eslint-disable-next-line no-magic-numbers
                take(1)
              )
              .subscribe((_accountInfo) => {
                if (isPlatformBrowser(this.platformId)) {
                  this.serverLoggerService.error('TIER1 SIGNUP: AccountInfo API Failed.', {
                    correlationId: autoLoginRedirect,
                  });
                }
                this.logout();
              });
          })
          .catch((err) => {
            if (isPlatformBrowser(this.platformId)) {
              this.serverLoggerService.error('TIER1 SIGNUP: silent refresh failed', {
                ...err,
                correlationId: autoLoginRedirect,
              });
            }
          });
        setTimeout(
          () => this.login(redirectPath, null, false, isTierThreeSignupForPasswordLessFlow, isBatchTier2Registration),
          logRequestWaitingTime
        );
      })
    );
  }

  navigateToExternalAddress() {
    const location = `/${environment
      .baseHref()
      .replace(new RegExp('/', 'g'), '')}/${CHECKOUT_STEPS_ROUTE}/${ORDER_STEP_ADDRESS}`;
    window.location.href = location;
  }

  logoutListener() {
    return this.logoutEvent$.asObservable();
  }

  public registerTierTwoAccount(
    registrationData: TierTwoRegistrationData,
    saveLegalAsBillingAndShipping?: boolean,
    extraRedirectParams = {}
  ): Observable<AccountRegistrationResponse> {
    return this.activeFeatureToggleService
      .isFeatureEnabledReadyForCurrentCountry$(FeatureToggleEnum.FF_TIER_TWO_ENDPOINT_CSS_TO_BUYER_ACCOUNT)
      .pipe(
        map((enabled) => (enabled ? authorizationEndpoints().tierTwoAccount : authCSSEndpoints().tierTwoAccount)),
        switchMap((url) =>
          this.httpClient.post<AccountRegistrationResponse>(url, registrationData).pipe(
            mergeMap((response: AccountRegistrationSuccess) =>
              this.switchNewContext(response).pipe(map((c) => response))
            ),
            mergeMap((response: AccountRegistrationSuccess) =>
              this.saveBillingAndShippingAddresses(
                response.accountId,
                registrationData,
                saveLegalAsBillingAndShipping
              ).pipe(
                tap(() => {
                  this.redirectAfterSuccess(extraRedirectParams);
                  this.showSuccessRegistrationAlert();
                }),
                map(() => response)
              )
            )
          )
        )
      );
  }

  public switchContext(accountId: string): Observable<IAccountInformation | IAccountInformationV2> {
    return this.httpClient
      .put(authCSSEndpoints().setActiveContext, {
        activeContext: accountId,
      })
      .pipe(switchMap(() => this.accountInfoService.fetchAccountInfo()));
  }

  public showContextSwitchError(): void {
    const failToSwitchMessages = this.translateService.get(AUTHORIZATION_NOTIFICATIONS.contextSwitchError);
    failToSwitchMessages.subscribe((message) => {
      this.alertService.addAlert({
        message,
        type: 'error',
        mode: 'banner',
        size: 'large',
        contentAlign: 'center',
      });
    });
  }

  public showSuccessRegistrationAlert(): void {
    const successRegistrationMessage$ = this.translateService.get(AUTHORIZATION_NOTIFICATIONS.successRegistration);
    const accountValidationMessage$ = this.translateService.get(AUTHORIZATION_NOTIFICATIONS.accountValidation);

    combineLatest([successRegistrationMessage$, accountValidationMessage$]).subscribe(
      ([successRegistrationMessage, accountValidationMessage]) => {
        this.alertService.addAlert({
          title: successRegistrationMessage,
          message: accountValidationMessage,
          type: 'success',
          mode: 'banner',
          size: 'large',
          contentAlign: 'center',
        });
      }
    );
  }

  public sendMagicLink(
    payload: Partial<IMagicLinkPayload> &
      Pick<IMagicLinkPayload, 'email'> & { backUrl?: string; expirationRoute?: string }
  ) {
    const {
      email,
      backUrl,
      expirationRoute,
      redirectionUrl = UrlUtil.generateRedirectionUrl(backUrl),
      expirationUrl = `${
        expirationRoute ? UrlUtil.generateRedirectionUrl(expirationRoute) : redirectionUrl
      }?${MAGIC_LINK_EXPIRY_QUERY_PARAM}=true`,
      countryCode = this.currentCountry?.toUpperCase(),
      locale = MarketDetectionUtil.getIDAMLocale(),
      realmId = environment.IDAMSSO().realm,
    } = payload;

    return this.authorizationHttpService.sendMagicLink({
      email,
      realmId,
      redirectionUrl,
      expirationUrl,
      locale,
      countryCode,
    });
  }

  public sendResetPassword(
    payload: Partial<IResetPasswordPayload> &
      Pick<IResetPasswordPayload, 'email'> & { backUrl?: string; expirationRoute?: string }
  ) {
    const {
      email,
      userType = 'CUST',
      backUrl,
      successUrl = `${UrlUtil.generateRedirectionUrl(backUrl)}?${RESET_PASSWORD_SUCCESS_QUERY_PARAM}=true`,
      expirationRoute,
      expirationUrl = `${
        expirationRoute ? UrlUtil.generateRedirectionUrl(expirationRoute) : successUrl
      }?${RESET_PASSWORD_SUCCESS_QUERY_PARAM}=false`,
      realmId = environment.IDAMSSO().realm,
      clientId = environment.IDAMSSO().clientId,
      countryCode = this.currentCountry,
      localeId = MarketDetectionUtil.getIDAMLocale(),
    } = payload;

    return this.authorizationHttpService.sendResetPassword({
      email,
      userType,
      successUrl,
      expirationUrl,
      realmId,
      clientId,
      countryCode,
      localeId,
    });
  }

  private saveBillingAndShippingAddresses(
    accountId: string,
    registrationData: TierTwoRegistrationData,
    saveLegalAsBillingAndShipping: boolean
  ) {
    const countryCode = this.currentCountry;
    if (IS_SAVE_LEGAL_ADDRESS_AS_BILLING_AND_SHIPPING[countryCode] && saveLegalAsBillingAndShipping) {
      const address = {
        id: accountId,
        companyName: registrationData.businessName,
        houseNumber: registrationData.legalAddressBuildingNumber,
        firstName: registrationData.firstName,
        lastName: registrationData.lastName,
        addressLine2: '',
        city: registrationData.legalAddressCity,
        country: countryCode,
        postalCode: registrationData.legalAddressPostalCode,
        street: registrationData.legalAddressStreet,
        defaultShipping: true,
        defaultBilling: true,
        ...(this.currentCountry === CountryCode.Italy && { stateOrProvince: registrationData.legalAddressProvince }),
      };
      return this.addressMetroXHttpService.createShippingAndBillingAddressV2(address);
    } else {
      return of(null);
    }
  }

  private redirectAfterSuccess(extraRedirectParams): void {
    const redirectUrlString = this.route.snapshot.queryParams.backUrl || '/';
    const redirectUrl = new Url(redirectUrlString, true);

    this.activeFeatureToggleService
      .isFeatureEnabledReadyForCurrentCountry$(FeatureToggleEnum.FF_SL_NG_REGISTER_EXTERNAL_REDIRECTION)
      .subscribe((enabled) => {
        if (enabled) {
          UrlUtil.redirectToExternalPage(redirectUrl.pathname, {
            ...this.getQueryParams(redirectUrl),
            ...extraRedirectParams,
          });
        } else {
          this.activeFeatureToggleService
            .isFeatureEnabledReadyForCurrentCountry$(FeatureToggleEnum.FF_EXTERNAL_CHECKOUT_ADDRESS_STEP)
            .subscribe((enabled) => {
              if (enabled && redirectUrl.pathname === '/checkout/checkout/order/address') {
                this.navigateToExternalAddress();
              } else if (redirectUrlString === '/') {
                UrlUtil.redirectToReactHomePage({
                  ...redirectUrl.query,
                  ...extraRedirectParams,
                });
              } else {
                this.router.navigate([redirectUrl.pathname], {
                  replaceUrl: true,
                  queryParams: this.getQueryParams(redirectUrl),
                });
              }
            });
        }
      });
  }

  private switchNewContext(
    context: AccountRegistrationSuccess
  ): Observable<IAccountInformation | IAccountInformationV2> {
    return this.accountFlyout
      .pollFlyoutStatusUpdate$(context.flyoutHandle)
      .pipe(
        takeUntil(this.contextSwitch$),
        catchError((err) => {
          this.showContextSwitchError();
          this.contextSwitch$.next();
          return err;
        }),
        concatMap(() => this.switchContext(context.accountId))
      )
      .pipe(
        tap(() => {
          this.contextSwitch$.next();
          this.identityService.syncAccountInfo();
          this.refreshFlyout(context.accountId);
        })
      );
  }

  private subscribeToRegisterCardGAEvent(): void {
    combineLatest([
      this.router.events,
      this.activeFeatureToggleService.isFeatureEnabled(FeatureToggleEnum.FF_CARD_REGISTER_TRACKING),
    ])
      .pipe(
        filter(([event, isEnabled]: [RouterEvent, boolean]) => isEnabled && event instanceof NavigationEnd),
        // eslint-disable-next-line no-magic-numbers
        take(1)
      )
      .subscribe(() => {
        const params = this.route.snapshot.queryParams;
        if (params.cardRegister) {
          const entries = Object.entries(params);

          const isPasswordless = entries.some(([key, value]) => key === 'passwordless' && value === 'true');
          const queryParams = entries
            .filter(([key]) => key !== 'cardRegister')
            // eslint-disable-next-line no-magic-numbers
            .reduce((acc, entry) => ({ ...acc, [entry[0]]: entry[1] }), {});
          this.applicationEventService.publish(
            new IdamRegisterApplicationEvent(
              ApplicationIdamRegisterEventTypeEnum.signUp,
              AuthType.AccountCreated,
              isPasswordless ? `Passwordless - ${RegistrationTypeLabels.Metro}` : `Passwordless - ${RegistrationTypeLabels.Metro}`
            ),
            true
          );
          this.router.navigate([], { relativeTo: this.route, queryParams });
        }
      });
  }

  private getURLTree(params, registerBusiness, backUrl) {
    if (registerBusiness) {
      return this.router.createUrlTree([ACCOUNT_ADD_BUSINESS_LINK], { queryParams: params });
    }

    if (this.isReactPLPsEnabled && backUrl.includes(`/${APP_CATALOG_ROUTE}`)) {
      const queryParams = Object.keys(params)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');

      const url = `${backUrl}?${queryParams}`;

      window.location.href = url;
    } else {
      return this.router.createUrlTree([backUrl], {
        queryParams: Object.keys(params).reduce((acc, key) => ({ ...acc, [key]: params[key] }), {}),
      });
    }
  }

  private refreshFlyout(accountId: string): void {
    this.isWebFlyoutEnabled$.subscribe((isWebFlyoutEnabled: boolean) => {
      if (isWebFlyoutEnabled) {
        AccountFlyoutUtils.refreshFlyout(accountId);
      } else {
        this.accountFlyoutFailover.loadAccountFlyoutData();
      }
    });
  }

  private getQueryParams(redirectUrl: Url<Record<string, string>>): Record<string, string> {
    if (!redirectUrl?.query?.backUrl) {
      const backUrl = localStorage.getItem(BACK_URL_KEY) ?? '/';
      return { ...(redirectUrl?.query ?? {}), backUrl };
    }

    return redirectUrl?.query ?? {};
  }

  private triggerLoginAfterSignup(
    loginId,
    password,
    userChallenge,
    redirectUri,
    autoLoginRedirectURI,
    autoLoginRedirect,
    passwordless
  ): Observable<any> {
    const params = {
      fromObject: {
        login_id: loginId,
        user_challenge: userChallenge,
        client_id: environment.IDAMSSO() && environment.IDAMSSO().clientId,
        realm_id: this.IDAM_SSO_REALM,
        country_code: this.currentCountry.toUpperCase(),
        user_type: USER_TYPE,
        response_type: 'code',
        redirect_uri: redirectUri,
      },
      encoder: this.queryParamsEncoder,
    };
    if (!passwordless) {
      Object.assign(params.fromObject, { password });
    }
    const body = new HttpParams(params);
    return this.authorizationHttpService.idamProcessSignedUp(
      this.oAuthService.issuer,
      body,
      autoLoginRedirectURI,
      autoLoginRedirect
    );
  }

  private getRedirectURI(registerBusiness: boolean): string[] {
    const backUrl = this.getBackUrl();
    const autoLoginRedirect = uuidv4();

    const queryParams = {
      backUrl,
    };

    const autoLoginFailQueryParam = {
      backUrl,
      autoLoginRedirect,
    };

    const urlTree = this.getURLTree(queryParams, registerBusiness, backUrl);

    const autoLoginUrlTree = this.getURLTree(autoLoginFailQueryParam, registerBusiness, backUrl);

    const redirectPath = `${environment.baseHref() || '/'}${this.serializer.serialize(urlTree)}`.replace('//', '/');

    const autologinRedirectPath = `${environment.baseHref() || '/'}${this.serializer.serialize(
      autoLoginUrlTree
    )}`.replace('//', '/');

    return [
      `${location.origin}${redirectPath}`,
      this.serializer.serialize(urlTree),
      `${location.origin}${autologinRedirectPath}`,
      autoLoginRedirect,
    ];
  }

  private getRedirectURIForPasswordLessFlow(
    isBatchTier2Registration = false,
    isTierThreeSignupForPasswordLessFlow = false,
    tier2PasswordlessRedirectionRoute?: string
  ): string[] {
    const backUrl = this.getBackUrl();
    const autoLoginRedirect = uuidv4();
    const queryParams = {
      backUrl,
    };

    const autoLoginFailQueryParam = {
      backUrl,
      autoLoginRedirect,
    };

    const urlTree = this.getURLTreeForPasswordLessFlow(
      queryParams,
      backUrl,
      false,
      isBatchTier2Registration,
      isTierThreeSignupForPasswordLessFlow,
      tier2PasswordlessRedirectionRoute
    );
    const autoLoginUrlTree = this.getURLTreeForPasswordLessFlow(
      autoLoginFailQueryParam,
      backUrl,
      true,
      isBatchTier2Registration,
      isTierThreeSignupForPasswordLessFlow,
      tier2PasswordlessRedirectionRoute
    );

    const baseUrl = isBatchTier2Registration ? environment.appBaseUrl() : environment.myMetroBaseUrl();
    const redirectPath = `${baseUrl}${this.serializer.serialize(urlTree)}`;
    const autologinRedirectPath = `${baseUrl}${this.serializer.serialize(autoLoginUrlTree)}`;

    return [`${redirectPath}`, this.serializer.serialize(urlTree), `${autologinRedirectPath}`, autoLoginRedirect];
  }

  private getURLTreeForPasswordLessFlow(
    params,
    backUrl,
    autoLogin?: boolean,
    isBatchTier2Registration = false,
    isTierThreeSignupForPasswordLessFlow = false,
    tier2PasswordlessRedirectionRoute?: string
  ) {
    const redirectUrl = UrlUtil.getAbsoluteUrlWithPath(backUrl.substring(NUMBER_1), {
      refreshflyout: 1,
      cardRegister: 1,
      passwordless: 'true',
    });

    const { autoLoginRedirect } = params;
    const autoLoginRedirectUrl = UrlUtil.getAbsoluteUrlWithPath(backUrl.substring(NUMBER_1), {
      autoLoginRedirect,
      backUrl,
      refreshflyout: 1,
      cardRegister: 1,
      passwordless: 'true',
    });

    const url = isBatchTier2Registration
      ? tier2PasswordlessRedirectionRoute ?? PASSWORDLESS_INTERMEDIATE_URL
      : ADD_METRO_CARD_URL;

    const redirectParams: Record<string, string> = {
      redirectUrl,
    };

    const autoLoginRedirectParams: Record<string, string> = {
      autoLoginRedirectUrl,
    };

    if (isTierThreeSignupForPasswordLessFlow) {
      return this.router.createUrlTree([url], {
        queryParams: redirectParams,
      });
    }

    if (isBatchTier2Registration) {
      redirectParams.redirectUrl = undefined;
      redirectParams.backUrl = backUrl;
      redirectParams.passwordless = 'true';
      autoLoginRedirectParams.autoLoginRedirectUrl = undefined;
    }

    if (autoLogin) {
      autoLoginRedirectParams.backUrl = backUrl;
      return this.router.createUrlTree([url], {
        queryParams: autoLoginRedirectParams,
      });
    }

    return this.router.createUrlTree([url], {
      queryParams: redirectParams,
    });
  }

  private getBackUrl() {
    return (
      this.route.snapshot.queryParams.backUrl ||
      localStorage.getItem(BACK_URL_KEY) ||
      localStorage.getItem(SIGNUP_SUCCESS_REDIRECT_PATH_KEY) ||
      '/'
    );
  }
}
